import type {
  BrandMemoryEvidence,
  BrandMemoryEvidenceAction,
  BrandMemoryKind,
  BrandMemoryRead,
  BrandMemoryScope,
  BrandSuggestionRead,
  MemoryArm,
} from '../../types/api';
import { PENDING_SIGNALS_CAP } from './brandMemoryApi';

/**
 * Plain functions over brand memories: the words the screen uses for them and
 * the order and groups it shows them in. No React, no API.
 */

/** Scope select and chip wording, in the order the add form lists them. */
export const BRAND_MEMORY_SCOPE_OPTIONS: readonly { value: BrandMemoryScope; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'copy', label: 'Captions' },
  { value: 'visual', label: 'Visuals' },
  { value: 'planning', label: 'Planning' },
];

/** Kind select and chip wording. `rule` reads "Always" because that is how the prompt says it. */
export const BRAND_MEMORY_KIND_OPTIONS: readonly { value: BrandMemoryKind; label: string }[] = [
  { value: 'prefer', label: 'Prefer' },
  { value: 'avoid', label: 'Avoid' },
  { value: 'rule', label: 'Always' },
];

export function scopeLabel(scope: BrandMemoryScope): string {
  return BRAND_MEMORY_SCOPE_OPTIONS.find((option) => option.value === scope)?.label ?? scope;
}

export function kindLabel(kind: BrandMemoryKind): string {
  return BRAND_MEMORY_KIND_OPTIONS.find((option) => option.value === kind)?.label ?? kind;
}

const EVIDENCE_ACTION_LABEL: Readonly<Record<string, string>> = {
  caption_edited: 'Caption edited',
  rejected: 'Sent back',
  revised: 'Revision asked for',
  variant_selected: 'Caption picked',
  approved: 'Approved',
  layer_edited: 'Slides edited',
  theme_changed: 'Template changed',
  redesigned: 'Slides redesigned',
  proposal_applied: 'Plan applied',
  proposal_dismissed: 'Plan dismissed',
};

/** A signal in words. An action this build does not know reads as its slug, spaced. */
export function evidenceActionLabel(action: BrandMemoryEvidenceAction): string {
  return EVIDENCE_ACTION_LABEL[action] ?? action.replace(/_/g, ' ');
}

/**
 * Where a signal happened: the assistant for a plan mark (planner chats were
 * folded into assistant threads with no id mapping, so the bare page), the
 * request's review screen for a review action. Null when the entry names neither.
 */
export function evidenceHref(entry: BrandMemoryEvidence): string | null {
  if (entry.session_id) return '/assistant';
  if (entry.request_id) return `/review/${entry.request_id}`;
  return null;
}

/** A list key for one evidence entry; the index breaks exact repeats. */
export function evidenceKey(entry: BrandMemoryEvidence, index: number): string {
  return `${entry.session_id ?? entry.request_id ?? 'none'}-${entry.action}-${entry.at}-${index}`;
}

/**
 * A suggestion as one sentence: `Never say "game changer"`, `Avoid the Clay 3D
 * picture style`, `Prefer the Analog film picture style`. The label is the
 * server's (quotes included for a phrase).
 */
export function suggestionHeadline(suggestion: BrandSuggestionRead): string {
  const label = suggestion.label;
  switch (suggestion.kind) {
    case 'never_say':
      return `Never say ${label}`;
    case 'avoid_picture_style':
      return `Avoid the ${label} picture style`;
    case 'prefer_picture_style':
      return `Prefer the ${label} picture style`;
    default:
      // A kind a later backend adds still shows its label.
      return label;
  }
}

/** The short confirmation after an accept: where the change landed. */
export function suggestionAcceptedNotice(suggestion: BrandSuggestionRead): string {
  const label = suggestion.label;
  switch (suggestion.kind) {
    case 'never_say':
      return `Added ${label} to your words to avoid.`;
    case 'avoid_picture_style':
      return `${label} picture style is now set to Avoid.`;
    case 'prefer_picture_style':
      return `${label} picture style is now set to Use.`;
    default:
      return 'Saved to your brand.';
  }
}

/** "You said", or "Learned from 3 posts". */
export function sourceBadge(memory: BrandMemoryRead): string {
  if (memory.source === 'stated') return 'You said';
  const count = memory.evidence_count;
  return `Learned from ${count} ${count === 1 ? 'post' : 'posts'}`;
}

/** "No new signals", "1 new signal", "12 new signals", "100+ new signals". */
export function pendingSignalsLabel(count: number): string {
  if (count <= 0) return 'No new signals';
  if (count >= PENDING_SIGNALS_CAP) return `${PENDING_SIGNALS_CAP}+ new signals`;
  return count === 1 ? '1 new signal' : `${count} new signals`;
}

/**
 * The list endpoint's own order: pinned first, then stated before learned,
 * then most evidence, then newest. Applied locally too, so a pin or an edit
 * lands where a reload would put it.
 */
export function compareBrandMemories(left: BrandMemoryRead, right: BrandMemoryRead): number {
  if (left.pinned !== right.pinned) return left.pinned ? -1 : 1;
  if (left.source !== right.source) return left.source === 'stated' ? -1 : 1;
  if (left.evidence_count !== right.evidence_count) {
    return right.evidence_count - left.evidence_count;
  }
  return right.created_at.localeCompare(left.created_at);
}

export interface BrandMemoryGroups {
  /** Candidates: learned from one post, waiting on the owner. */
  suggestions: BrandMemoryRead[];
  active: BrandMemoryRead[];
  /** Retired, by the owner or by Autopostmate. */
  forgotten: BrandMemoryRead[];
}

export function groupBrandMemories(memories: readonly BrandMemoryRead[]): BrandMemoryGroups {
  const sorted = [...memories].sort(compareBrandMemories);
  return {
    suggestions: sorted.filter((memory) => memory.status === 'candidate'),
    active: sorted.filter((memory) => memory.status === 'active'),
    forgotten: sorted.filter((memory) => memory.status === 'retired'),
  };
}

function countPhrase(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * The review screen's muted line: "Shaped by 4 brand memories and 2 approved
 * posts". A zero (or missing) half is left out; null when both are, and on a
 * `holdout` or `off` run.
 */
export function shapedByLine(
  memoryCount: number | null | undefined,
  exampleCount: number | null | undefined,
  memoryArm: MemoryArm | null | undefined = null,
): string | null {
  // A held-out run, or one from a brand with memory switched off, carried
  // nothing to be shaped by, whatever the counts say.
  if (memoryArm === 'holdout' || memoryArm === 'off') return null;
  const parts: string[] = [];
  if (memoryCount) parts.push(countPhrase(memoryCount, 'brand memory', 'brand memories'));
  if (exampleCount) parts.push(countPhrase(exampleCount, 'approved post', 'approved posts'));
  return parts.length === 0 ? null : `Shaped by ${parts.join(' and ')}`;
}
